/**
 * The expanded edit card: the chrome around one slot's editable body.
 *
 * Header, footer and the Insert menu are the same for every type — only the
 * body between them differs, and `SlotViews` picks that. The card commits on
 * collapse: every keystroke has already written through to the slot, so the ✓
 * in the header only leaves edit mode. A Select or Fill in slot cannot leave it
 * with a blank prompt — the prompt is what the sender is asked, and what the
 * Settings tab lists the slot by — so ✓, Duplicate and Link wait for one.
 */

import React, { useRef, useState } from 'react';
import { IonActionSheet, IonAlert, IonIcon } from '@ionic/react';
import {
  checkmarkOutline,
  copyOutline,
  linkOutline,
  trashOutline,
} from 'ionicons/icons';
import { insertableNames } from './schema';
import type { MessageIntent } from '@/modules/template/types';
import type { TopicTypeId } from '@/modules/topic/types';
import { SlotLogic, type SlotType } from './slot_logic';
import type {
  SlotEditorProps,
  SlotLinkProps,
  SlotLinkTarget,
} from './SlotEditors';
import {
  SlotBadge,
  SlotEditorBody,
  insertableTextOf,
  typeClass,
  withInsertedText,
} from './SlotViews';
import styles from './slots.module.css';

/**
 * What a text or goal slot can have put into it: a line break, then every primitive
 * the app resolves when the message is composed. The primitives are
 * hard to remember and easy to misspell, and a typo only shows up as literal
 * braces in a real message — so they are offered here rather than typed.
 *
 * A sheet rather than a popover, because that list is long: a sheet gives it
 * the whole width, room to scroll, and touch targets a thumb can hit. It is
 * also what the builder already opens for the other long list of choices, the
 * one behind "Add slot".
 */
const InsertMenu: React.FC<{
  testId: string;
  onOpen: () => void;
  onInsert: (insertion: string) => void;
  /** The topic's type, whose defined names the menu offers alongside the primitives. */
  topicType?: TopicTypeId;
}> = ({ testId, onOpen, onInsert, topicType }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={styles.insertButton}
        // Pressing this must not move focus out of the field, or the caret the
        // insert is aimed at would be gone before it could be recorded.
        onMouseDown={e => e.preventDefault()}
        onClick={() => {
          onOpen();
          setOpen(true);
        }}
        data-testid={`${testId}-insert`}
      >
        Insert ▾
      </button>
      <IonActionSheet
        isOpen={open}
        onDidDismiss={() => setOpen(false)}
        header="Insert"
        data-testid={`${testId}-insert-sheet`}
        buttons={[
          { text: 'Line break', handler: () => onInsert('\n') },
          ...insertableNames(topicType).map(({ name, doc }) => ({
            text: doc === undefined ? `{${name}}` : `{${name}} · ${doc}`,
            handler: () => onInsert(`{${name}}`),
          })),
          { text: 'Cancel', role: 'cancel' as const },
        ]}
      />
    </>
  );
};

const EditCardHeader: React.FC<{
  type: SlotType;
  testId: string;
  canCommit: boolean;
  onCommit: () => void;
}> = ({ type, testId, canCommit, onCommit }) => (
  <div className={styles.editHeader}>
    <SlotBadge type={type} />
    <span className={styles.editEyebrow}>{SlotLogic.typeName(type)}</span>
    <button
      type="button"
      className={styles.commitButton}
      onClick={onCommit}
      disabled={!canCommit}
      aria-label={`Done editing ${SlotLogic.typeName(type).toLowerCase()}`}
      data-testid={`${testId}-commit`}
    >
      <IonIcon icon={checkmarkOutline} className={styles.commitIcon} />
    </button>
  </div>
);

/**
 * Which other intents' forms hold a slot linked to this one, so the author can
 * tell on sight that it is asked there too. Shown on the collapsed card and
 * the open one alike.
 */
export const LinkedNote: React.FC<{
  linkedWith: readonly SlotLinkTarget[];
  testId: string;
}> = ({ linkedWith, testId }) =>
  linkedWith.length === 0 ? null : (
    <span className={styles.linkedNote} data-testid={`${testId}-linked`}>
      <IonIcon icon={linkOutline} aria-hidden="true" />
      Linked with {linkedWith.map(t => t.label).join(', ')}
    </span>
  );

/**
 * Choose which intents the slot is linked across. The popup lists every
 * intent with a checkmark on the slot's own and the ones already linked; Done
 * links exactly the ticked ones, copying the slot into any that lack it and
 * leaving an unticked one's slot behind as an independent copy. Unticking the
 * slot's own intent takes just this slot out of the set, leaving the others
 * linked, and unticking everything unlinks the whole set. There is no Cancel:
 * undoing a tick is tapping it again.
 */
const LinkButton: React.FC<{
  testId: string;
  enabled: boolean;
  link: SlotLinkProps;
}> = ({ testId, enabled, link }) => {
  const [open, setOpen] = useState(false);
  const linked = new Set([link.home, ...link.linkedWith.map(t => t.intent)]);
  return (
    <>
      <button
        type="button"
        className={styles.iconButton}
        onClick={() => setOpen(true)}
        disabled={!enabled || link.targets.length < 2}
        aria-label="Link slot with other messages"
        data-testid={`${testId}-link`}
      >
        <IonIcon icon={linkOutline} />
      </button>
      <IonAlert
        isOpen={open}
        onDidDismiss={() => setOpen(false)}
        // Done is the only way out, so a stray tap outside never
        // throws away the checkmarks the author just set.
        backdropDismiss={false}
        header="Link with"
        message="Linked slots are the same question in each message, so the sender answers it once."
        data-testid={`${testId}-link-popup`}
        inputs={link.targets.map(target => ({
          type: 'checkbox' as const,
          label: target.label,
          value: target.intent,
          checked: linked.has(target.intent),
          attributes: { 'data-testid': `${testId}-link-${target.intent}` },
        }))}
        buttons={[
          {
            text: 'Done',
            // The ticked inputs' values, which are the targets' intents.
            handler: (intents: MessageIntent[] | undefined) =>
              link.onSetLinks(intents ?? []),
            htmlAttributes: { 'data-testid': `${testId}-link-done` },
          },
        ]}
      />
    </>
  );
};

const EditCardFooter: React.FC<{
  testId: string;
  canDuplicate: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
  link?: SlotLinkProps;
  /** Editor-specific actions, kept to the left of the shared ones. */
  leading?: React.ReactNode;
}> = ({ testId, canDuplicate, onDuplicate, onDelete, link, leading }) => (
  <div className={styles.editFooter}>
    {leading ? (
      <span className={styles.editFooterLeading}>{leading}</span>
    ) : null}
    {link && <LinkButton testId={testId} enabled={canDuplicate} link={link} />}
    <button
      type="button"
      className={styles.iconButton}
      onClick={onDuplicate}
      disabled={!canDuplicate}
      aria-label="Duplicate slot"
      data-testid={`${testId}-duplicate`}
    >
      <IonIcon icon={copyOutline} />
    </button>
    <button
      type="button"
      className={styles.iconButton}
      onClick={onDelete}
      aria-label="Delete slot"
      data-testid={`${testId}-delete`}
    >
      <IonIcon icon={trashOutline} />
    </button>
  </div>
);

/**
 * Put something in at the caret, and leave the caret after it, so a second
 * insert does not land back where the first one did.
 *
 * The caret is whatever the menu recorded as it opened, since opening it moves
 * focus into the popover. No caret means the field was never touched — an
 * untouched textarea still reports a selection of 0, which would silently put
 * every break at the very start — so the break goes on the end instead.
 */
function insertAtCaret(
  props: SlotEditorProps,
  field: HTMLTextAreaElement | null,
  caret: { at: number; to: number } | null,
  insertion: string
) {
  const slot = props.slot;
  const text = insertableTextOf(slot);
  if (text === null) return;
  const at = caret?.at ?? text.length;
  const to = caret?.to ?? text.length;
  props.onChange(
    withInsertedText(slot, `${text.slice(0, at)}${insertion}${text.slice(to)}`)
  );
  if (!field) return;
  // The value lands on the next render, so the caret has to wait for it.
  const after = at + insertion.length;
  requestAnimationFrame(() => {
    field.focus();
    field.setSelectionRange(after, after);
  });
}

export const SlotEditor: React.FC<SlotEditorProps> = props => {
  const type = SlotLogic.typeOf(props.slot);
  const fieldRef = useRef<HTMLTextAreaElement | null>(null);
  const caretRef = useRef<{ at: number; to: number } | null>(null);
  const canClose = !SlotLogic.lacksPrompt(props.slot);
  return (
    <div
      className={`${styles.editCard} ${typeClass(type)}`}
      data-testid={props.testId}
    >
      <EditCardHeader
        type={type}
        testId={props.testId}
        canCommit={canClose}
        onCommit={props.onCommit}
      />
      <SlotEditorBody {...props} fieldRef={fieldRef} />
      {props.link && (
        <LinkedNote linkedWith={props.link.linkedWith} testId={props.testId} />
      )}
      <EditCardFooter
        testId={props.testId}
        canDuplicate={canClose}
        onDuplicate={props.onDuplicate}
        onDelete={props.onDelete}
        link={props.link}
        leading={
          insertableTextOf(props.slot) !== null ? (
            <InsertMenu
              testId={props.testId}
              topicType={props.context.topicType}
              onOpen={() => {
                // The popover takes focus, so the caret has to be remembered
                // now — and only when the field actually had one, since an
                // untouched textarea still reports a selection of 0.
                const field = fieldRef.current;
                caretRef.current =
                  field && document.activeElement === field
                    ? { at: field.selectionStart, to: field.selectionEnd }
                    : null;
              }}
              onInsert={insertion =>
                insertAtCaret(
                  props,
                  fieldRef.current,
                  caretRef.current,
                  insertion
                )
              }
            />
          ) : null
        }
      />
    </div>
  );
};
